// Pointer-driven drag for unified `.pane` UI chrome. Movement during drag
// uses CSS custom properties `--drag-x` / `--drag-y` consumed by a
// `transform: translate(...)` in style.css. On pointerup-after-drag we
// (a) magnetically snap to the nearest viewport edge or corner if the pane
// is within EDGE_SNAP_PX, (b) update the `data-edge` attribute so the pane
// chrome reorients, and (c) fire a `paneedgechange` CustomEvent so dependent
// renderers (colorbar gradient) can repaint in the new orientation.

const DRAG_THRESHOLD_PX = 4;
const VIEWPORT_MARGIN_PX = 8;
const EDGE_SNAP_PX = 60;
const CORNER_SNAP_PX = 80;
// Minimum width of the drag handle that must remain inside the viewport
// for any free-floating panel (help modal etc.). Lets the user park the
// panel mostly off-screen along a side, as long as a grippable strip of
// the header is still reachable. Vertically the full handle stays in.
const MIN_HANDLE_VISIBLE_PX = 80;
// Match the static initial positions in style.css (top/right/bottom/left: 15px)
// so re-snapping a pane never visibly shifts it from its first-paint location.
const EDGE_GAP_PX = 15;
// Bias applied when picking which edge to dock against: a pane already on
// the `top` edge has to be `STICKY_HYSTERESIS_PX` further from top than
// from bottom before it flips to bottom (and vice versa). Without this,
// a pane tall enough that both distT and distB are small will oscillate
// between top↔bottom on tiny drags.
const STICKY_HYSTERESIS_PX = 120;

// Dynamic stacking for any draggable window — .pane elements *and* the
// help modal — within [Z_BASE, Z_CEILING]. Task indicator (z:1001) stays
// above the ceiling as a system overlay. Static base z-indices: Perf=9,
// Colorbar=11, Title=12, help modal=20 (= Z_BASE, so it lands above
// uninteracted panes on first paint). Counter renormalizes near the
// ceiling so the integer space stays bounded.
const Z_BASE = 20;
const Z_CEILING = 999;
// Selector matching every element that participates in the dynamic stack,
// for renormalizeZ. Add new draggable windows here so they don't get
// stranded at stale high z-indices after a renormalize.
const STACKED_SELECTOR =
    '.pane, #help-panel, .console-panel, .reductions-plot-panel, .selection-info-panel, .controls-flyout, .coord-popover';
let zCounter = Z_BASE;

// Tolerance for the collapsed-pane AABB overlap test — sub-pixel snap
// residue should not register as a collision.
const OVERLAP_EPSILON_PX = 0.5;

// Static chrome that a chrome-avoiding draggable (the colorbar — the only one
// today) must not cover when dropped. Opt in per pane via the
// `data-collision-chrome` attribute. `:not(.is-hidden)` skips the secondary
// colorbar and the controls flyout while they're hidden.
const CHROME_SELECTOR =
    '#topbar-menu, #controls-rail, #controls-flyout:not(.is-hidden), #bottom-cluster, #camera-gnomon, #perf-container, .colorbar-stack-secondary:not(.is-hidden)';

export type PaneEdge = 'left' | 'right' | 'top' | 'bottom';

export interface DragHandle {
    wasDragging: () => boolean;
}

export interface MakeDraggableOpts {
    /** Element that receives the pointerdown to initiate drag. Defaults to `pane`. */
    dragHandle?: HTMLElement;
}

/** Resolve the pane's current visual rect. */
function rectOf(panel: HTMLElement): DOMRect {
    return panel.getBoundingClientRect();
}

/** Post-style layout rect for `panel`, computed from inline anchors + offset
 *  dims. Bypasses CSS transforms (incl. the in-flight snap-back transform
 *  transition that immediately follows magneticSnap's writeOffset), so the
 *  result reflects the *target* layout box even mid-animation — unlike
 *  getBoundingClientRect, which composites the transform and would return
 *  the drag-end visual position. Falls back to the bounding rect for axes
 *  that have no inline anchor yet. */
function styledRect(panel: HTMLElement): { left: number; top: number; right: number; bottom: number; width: number; height: number } {
    const W = window.innerWidth, H = window.innerHeight;
    const w = panel.offsetWidth;
    const h = panel.offsetHeight;
    const sL = panel.style.left, sR = panel.style.right;
    const sT = panel.style.top,  sB = panel.style.bottom;
    let left: number, top: number;
    if (sL && sL !== 'auto')      left = parseFloat(sL);
    else if (sR && sR !== 'auto') left = W - parseFloat(sR) - w;
    else                          left = panel.getBoundingClientRect().left;
    if (sT && sT !== 'auto')      top = parseFloat(sT);
    else if (sB && sB !== 'auto') top = H - parseFloat(sB) - h;
    else                          top = panel.getBoundingClientRect().top;
    return { left, top, right: left + w, bottom: top + h, width: w, height: h };
}

/** Lift `panel` above every other window that has been interacted with.
 *  Idempotent per gesture — bumping the counter twice in one drag is fine,
 *  renormalizeZ() rebases when the counter nears Z_CEILING.
 *
 *  Floor: never writes below the element's current effective z-index. On
 *  first raise the floor is the computed CSS baseline (so an uninteracted
 *  element with a higher CSS z-index isn't accidentally demoted); on
 *  subsequent raises it is the inline value. In the common case where
 *  the counter has advanced past the floor, max() collapses to the
 *  counter and this is just defensive bookkeeping. */
export function raisePane(panel: HTMLElement): void {
    if (zCounter >= Z_CEILING) renormalizeZ();
    const next = ++zCounter;
    const inlineRaw = panel.style.zIndex;
    let floor: number;
    if (inlineRaw === '') {
        const computed = parseInt(getComputedStyle(panel).zIndex, 10);
        floor = Number.isFinite(computed) ? computed : Z_BASE;
    } else {
        const inlineZ = parseInt(inlineRaw, 10);
        floor = Number.isFinite(inlineZ) ? inlineZ : Z_BASE;
    }
    panel.style.zIndex = String(Math.max(next, floor));
}

/** Compact existing z-indices back to a fresh contiguous range — preserves
 *  current visual order, just rescales numbers so the counter can grow. */
function renormalizeZ(): void {
    const assigned = Array.from(document.querySelectorAll<HTMLElement>(STACKED_SELECTOR))
        .filter((p) => p.style.zIndex !== '')
        .sort((a, b) => parseInt(a.style.zIndex, 10) - parseInt(b.style.zIndex, 10));
    zCounter = Z_BASE;
    for (const p of assigned) { p.style.zIndex = String(++zCounter); }
}

function readVar(panel: HTMLElement, name: string): number {
    return parseFloat(panel.style.getPropertyValue(name) || '0') || 0;
}

// Body-level selection lock used by every gesture in this module (pane drag,
// free drag, corner resize). Without it, the pointer can sweep through text
// in another pane / the same modal during a drag and the browser starts a
// selection — visually messy and persists after the gesture ends. Refcounted
// so overlapping gestures (rare but possible with multi-touch) don't unlock
// the body while one is still active.
let gestureLockCount = 0;
function lockBodySelection(): void {
    if (gestureLockCount === 0) document.body.classList.add('gesture-active');
    gestureLockCount++;
}
function unlockBodySelection(): void {
    if (gestureLockCount === 0) return;
    gestureLockCount--;
    if (gestureLockCount === 0) document.body.classList.remove('gesture-active');
}

function writeOffset(panel: HTMLElement, x: number, y: number): void {
    panel.style.setProperty('--drag-x', `${x}px`);
    panel.style.setProperty('--drag-y', `${y}px`);
}

/** Apply inline anchors so the pane sits at the current visual rect with
 *  axes anchored according to `horizAnchor` and `vertAnchor`. Anchoring to
 *  `right` / `bottom` means the pane will grow toward the viewport center
 *  when its width/height change (expand on collapse-toggle, etc) — which is
 *  the expected behaviour for an edge-docked pane. */
function setInlineAnchors(
    panel: HTMLElement,
    rect: DOMRect,
    horizAnchor: 'left' | 'right',
    vertAnchor: 'top' | 'bottom',
    overrideLeft?: number,
    overrideTop?: number,
): void {
    const W = window.innerWidth, H = window.innerHeight;
    const left = overrideLeft ?? rect.left;
    const top = overrideTop ?? rect.top;
    if (horizAnchor === 'left') {
        panel.style.left = `${left}px`;
        panel.style.right = 'auto';
    } else {
        panel.style.right = `${W - left - rect.width}px`;
        panel.style.left = 'auto';
    }
    if (vertAnchor === 'top') {
        panel.style.top = `${top}px`;
        panel.style.bottom = 'auto';
    } else {
        panel.style.bottom = `${H - top - rect.height}px`;
        panel.style.top = 'auto';
    }
}

/** Choose appropriate axis anchors based on the pane's current data-edge.
 *  Right-docked → anchor right (grows leftward on expand); bottom-docked →
 *  anchor bottom (grows upward); otherwise top/left. */
function anchorsForEdge(edge: PaneEdge | null | undefined): { h: 'left' | 'right'; v: 'top' | 'bottom' } {
    return {
        h: edge === 'right' ? 'right' : 'left',
        v: edge === 'bottom' ? 'bottom' : 'top',
    };
}

/** Edge-flip comparator with hysteresis: returns true iff `a < b`, but
 *  biased by STICKY_HYSTERESIS_PX in favor of the current edge so tall/wide
 *  panes don't oscillate between opposite edges on tiny drags. Caller maps
 *  the meaning of `a`/`b` (e.g. distT vs distB for vertical, distL vs distR
 *  for horizontal) and the corresponding low/high edges. */
function stickyLess(
    a: number, b: number,
    prevEdge: PaneEdge | undefined,
    lowEdge: PaneEdge, highEdge: PaneEdge,
): boolean {
    const HYST = STICKY_HYSTERESIS_PX;
    if (prevEdge === lowEdge)  return a <= b + HYST;
    if (prevEdge === highEdge) return a + HYST < b;
    return a <= b;
}

/** Magnetic snap on drag release. Returns the new edge if it changed,
 *  otherwise null. Mutates inline anchors so the pane docks flush to the
 *  nearest edge/corner with EDGE_GAP_PX of breathing room — using the
 *  edge as the anchor (so expand/collapse grows toward the center). */
function magneticSnap(panel: HTMLElement): PaneEdge | null {
    const r = rectOf(panel);
    const W = window.innerWidth;
    const H = window.innerHeight;
    const prevEdge = panel.dataset.edge as PaneEdge | undefined;

    const distL = r.left;
    const distR = W - r.right;
    const distT = r.top;
    const distB = H - r.bottom;

    const nearH = (distL < EDGE_SNAP_PX) || (distR < EDGE_SNAP_PX);
    const nearV = (distT < EDGE_SNAP_PX) || (distB < EDGE_SNAP_PX);
    const cornerNearH = (distL < CORNER_SNAP_PX) || (distR < CORNER_SNAP_PX);
    const cornerNearV = (distT < CORNER_SNAP_PX) || (distB < CORNER_SNAP_PX);

    let edge: PaneEdge | null = null;
    let horizAnchor: 'left' | 'right' = 'left';
    let vertAnchor: 'top' | 'bottom' = 'top';
    let leftPx = r.left;
    let topPx = r.top;
    let didSnap = false;

    if (cornerNearH && cornerNearV) {
        // Corner snap: pin both axes.
        const leftSide = stickyLess(distL, distR, prevEdge, 'left', 'right');
        const topSide = stickyLess(distT, distB, prevEdge, 'top', 'bottom');
        horizAnchor = leftSide ? 'left' : 'right';
        vertAnchor = topSide ? 'top' : 'bottom';
        leftPx = leftSide ? EDGE_GAP_PX : (W - r.width - EDGE_GAP_PX);
        topPx = topSide ? EDGE_GAP_PX : (H - r.height - EDGE_GAP_PX);
        // Orient along whichever edge gives the more natural collapse shape.
        edge = r.width >= r.height ? (topSide ? 'top' : 'bottom') : (leftSide ? 'left' : 'right');
        didSnap = true;
    } else if (nearH) {
        const leftSide = stickyLess(distL, distR, prevEdge, 'left', 'right');
        horizAnchor = leftSide ? 'left' : 'right';
        vertAnchor = 'top'; // free axis — preserve top
        leftPx = leftSide ? EDGE_GAP_PX : (W - r.width - EDGE_GAP_PX);
        topPx = clamp(r.top, VIEWPORT_MARGIN_PX, H - r.height - VIEWPORT_MARGIN_PX);
        edge = leftSide ? 'left' : 'right';
        didSnap = true;
    } else if (nearV) {
        const topSide = stickyLess(distT, distB, prevEdge, 'top', 'bottom');
        vertAnchor = topSide ? 'top' : 'bottom';
        horizAnchor = 'left';
        topPx = topSide ? EDGE_GAP_PX : (H - r.height - EDGE_GAP_PX);
        leftPx = clamp(r.left, VIEWPORT_MARGIN_PX, W - r.width - VIEWPORT_MARGIN_PX);
        edge = topSide ? 'top' : 'bottom';
        didSnap = true;
    }

    if (didSnap && edge !== null) {
        setInlineAnchors(panel, r, horizAnchor, vertAnchor, leftPx, topPx);
        writeOffset(panel, 0, 0);
        if (prevEdge !== edge) {
            panel.dataset.edge = edge;
            panel.dispatchEvent(new CustomEvent('paneedgechange', { detail: { edge } }));
            return edge;
        }
        return null;
    }

    // No snap → free drop. Flip data-edge along the pane's current axis
    // (horizontal panes flip left↔right at the horizontal midpoint;
    // vertical panes flip top↔bottom at the vertical midpoint) so chevron
    // direction, grip orientation, and collapse axis all reflect which
    // half of the viewport the pane currently lives in. Hysteresis here
    // mirrors the snap branches above.
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const verticalAxis = prevEdge === 'top' || prevEdge === 'bottom';
    const naturalEdge: PaneEdge = verticalAxis
        ? (stickyLess(cy, H - cy, prevEdge, 'top', 'bottom') ? 'top' : 'bottom')
        : (stickyLess(cx, W - cx, prevEdge, 'left', 'right') ? 'left' : 'right');
    const { h, v } = anchorsForEdge(naturalEdge);
    const clampedLeft = clamp(r.left, VIEWPORT_MARGIN_PX, W - r.width - VIEWPORT_MARGIN_PX);
    const clampedTop  = clamp(r.top,  VIEWPORT_MARGIN_PX, H - r.height - VIEWPORT_MARGIN_PX);
    setInlineAnchors(panel, r, h, v, clampedLeft, clampedTop);
    writeOffset(panel, 0, 0);
    if (prevEdge !== naturalEdge) {
        panel.dataset.edge = naturalEdge;
        panel.dispatchEvent(new CustomEvent('paneedgechange', { detail: { edge: naturalEdge } }));
        return naturalEdge;
    }
    return null;
}

function clamp(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, v));
}

interface RectBox { left: number; top: number; right: number; bottom: number; width: number; height: number; }

function overlapsRect(a: RectBox, b: RectBox): boolean {
    return a.left + OVERLAP_EPSILON_PX < b.right
        && a.right - OVERLAP_EPSILON_PX > b.left
        && a.top + OVERLAP_EPSILON_PX < b.bottom
        && a.bottom - OVERLAP_EPSILON_PX > b.top;
}

/** Single-axis pushes that would clear `r` from `o` *with* EDGE_GAP_PX of
 *  breathing room. When `edge` is set, only the axis ALONG that dock is
 *  returned — so a top-docked pane springs sideways (not down off the
 *  edge), and a left-docked pane springs vertically. Free-floating panes
 *  (no edge) get all four directions and the min-magnitude wins. */
function pushVectors(r: RectBox, o: RectBox, edge: PaneEdge | undefined): { dx: number; dy: number }[] {
    const G = EDGE_GAP_PX;
    const horiz = [
        { dx: (o.right - r.left)  + G, dy: 0 },            // push right
        { dx: (o.left  - r.right) - G, dy: 0 },            // push left
    ];
    const vert = [
        { dx: 0, dy: (o.bottom - r.top)    + G },          // push down
        { dx: 0, dy: (o.top    - r.bottom) - G },          // push up
    ];
    if (edge === 'top'  || edge === 'bottom') return horiz;
    if (edge === 'left' || edge === 'right')  return vert;
    return [...horiz, ...vert];
}

/** Spring `panel` out of overlaps to the nearest free screen position.
 *  Iterative push-out along the minimum-translation axis; bails after 8
 *  iterations on cramped mobile rather than thrashing. Reuses the .pane CSS
 *  transition on top/left for the spring-back animation.
 *
 *  Two obstacle sources, additive:
 *   - collapsed-vs-collapsed panes (only when `panel` is itself collapsed) —
 *     expanded panes may overlap freely;
 *   - static chrome (rail, menu pill, bottom cluster, gnomon, perf, a second
 *     colorbar) when `panel` opts in via `data-collision-chrome` — used by the
 *     colorbar so a user drag can't park it on top of the chrome. */
export function resolveCollapsedOverlap(panel: HTMLElement): void {
    const wantsChrome = panel.hasAttribute('data-collision-chrome');
    const isCollapsed = panel.classList.contains('collapsed');
    if (!isCollapsed && !wantsChrome) return;

    // `.pane` is position:fixed, so offsetParent is null even when visible.
    // Filter on rect area instead — zero-area means display:none / hidden.
    const obstacles: RectBox[] = [];
    const pushObstacle = (el: HTMLElement): void => {
        if (el === panel) return;
        const r = rectOf(el);
        if (r.width <= 0 || r.height <= 0) return;
        obstacles.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
    };
    if (isCollapsed) {
        for (const p of document.querySelectorAll<HTMLElement>('.pane.collapsed')) pushObstacle(p);
    }
    if (wantsChrome) {
        for (const el of document.querySelectorAll<HTMLElement>(CHROME_SELECTOR)) pushObstacle(el);
    }
    if (obstacles.length === 0) return;

    // Use the layout-derived rect for the dragged pane to bypass the in-flight
    // transform transition (drag-x/y → 0) that contaminates getBoundingClientRect
    // for the first ~300ms after magneticSnap.
    const r0 = styledRect(panel);
    const cur: RectBox = {
        left: r0.left, top: r0.top, right: r0.right, bottom: r0.bottom,
        width: r0.width, height: r0.height,
    };
    const dragEdge = panel.dataset.edge as PaneEdge | undefined;

    for (let iter = 0; iter < 8; iter++) {
        let best: { dx: number; dy: number } | null = null;
        let bestMag = Infinity;
        for (const o of obstacles) {
            if (!overlapsRect(cur, o)) continue;
            for (const v of pushVectors(cur, o, dragEdge)) {
                const m = Math.abs(v.dx) + Math.abs(v.dy);  // single-axis → L1 == L2
                if (m > 0 && m < bestMag) { bestMag = m; best = v; }
            }
        }
        if (!best) break;
        cur.left += best.dx; cur.right += best.dx;
        cur.top  += best.dy; cur.bottom += best.dy;
        // Keep inside viewport with the same safety margin as installResizeClamp.
        const W = window.innerWidth, H = window.innerHeight;
        const dxClamp = Math.max(VIEWPORT_MARGIN_PX - cur.left, Math.min(0, W - VIEWPORT_MARGIN_PX - cur.right));
        const dyClamp = Math.max(VIEWPORT_MARGIN_PX - cur.top,  Math.min(0, H - VIEWPORT_MARGIN_PX - cur.bottom));
        cur.left += dxClamp; cur.right += dxClamp;
        cur.top  += dyClamp; cur.bottom += dyClamp;
    }

    if (cur.left === r0.left && cur.top === r0.top) return;
    // Anchor side resolution: an explicit inline anchor wins; otherwise infer
    // from data-edge so a left/right-docked pane writes left/right (matching
    // the dock), and similarly for top/bottom. This matters for the collapse-
    // toggle path where the freshly-collapsed pane has no inline anchors yet —
    // writing to the wrong side leaves CSS `left:15` / `top:15` defaults to
    // override the inline value, pinning the pane at the corner. Also clear
    // the opposite axis so the CSS default can't fight the inline write.
    const W = window.innerWidth, H = window.innerHeight;
    const hasInlineLeft   = !!panel.style.left   && panel.style.left   !== 'auto';
    const hasInlineRight  = !!panel.style.right  && panel.style.right  !== 'auto';
    const hasInlineTop    = !!panel.style.top    && panel.style.top    !== 'auto';
    const hasInlineBottom = !!panel.style.bottom && panel.style.bottom !== 'auto';
    const usingLeft = hasInlineLeft  ? true  : (hasInlineRight  ? false : dragEdge !== 'right');
    const usingTop  = hasInlineTop   ? true  : (hasInlineBottom ? false : dragEdge !== 'bottom');
    if (usingLeft) { panel.style.left  = `${cur.left}px`;       panel.style.right  = 'auto'; }
    else           { panel.style.right = `${W - cur.right}px`;  panel.style.left   = 'auto'; }
    if (usingTop)  { panel.style.top    = `${cur.top}px`;       panel.style.bottom = 'auto'; }
    else           { panel.style.bottom = `${H - cur.bottom}px`; panel.style.top   = 'auto'; }
}

export function makeDraggable(panel: HTMLElement, opts: MakeDraggableOpts = {}): DragHandle {
    const handle = opts.dragHandle ?? panel;
    let startPointerX = 0, startPointerY = 0;
    let startDragX = 0, startDragY = 0;
    let active = false;
    let capturedPointerId: number | null = null;
    let recentlyDragged = false;

    const endDrag = (commit: boolean): void => {
        if (capturedPointerId !== null) {
            try { handle.releasePointerCapture(capturedPointerId); } catch { /* already released */ }
            capturedPointerId = null;
        }
        if (!active) return;
        active = false;
        panel.classList.remove('dragging');
        unlockBodySelection();
        if (commit) {
            magneticSnap(panel);
            // After snap places the pane, push it out of any collapsed-pane
            // collision (collapsed-vs-collapsed only; expanded may overlap).
            resolveCollapsedOverlap(panel);
            // Suppress the click that follows pointerup. Microtask is too
            // early — click fires before the next macrotask boundary.
            recentlyDragged = true;
            setTimeout(() => { recentlyDragged = false; }, 0);
        } else {
            // Aborted (blur / visibility): leave inline anchors alone, just
            // zero out the temporary drag offset so the transform formula
            // returns to baseline.
            writeOffset(panel, 0, 0);
        }
    };

    handle.addEventListener('pointerdown', (e) => {
        if (!e.isPrimary) return;
        const target = e.target as Element | null;
        // Don't initiate drag from interactive descendants. The chevron
        // toggle is the most important one to exclude.
        if (target?.closest('.pane-toggle, .collapse-btn, button, input, select, textarea, a, [data-no-drag]')) return;
        startPointerX = e.clientX;
        startPointerY = e.clientY;
        startDragX = readVar(panel, '--drag-x');
        startDragY = readVar(panel, '--drag-y');
        capturedPointerId = e.pointerId;
        handle.setPointerCapture(e.pointerId);
    });

    handle.addEventListener('pointermove', (e) => {
        if (capturedPointerId !== e.pointerId) return;
        const dx = e.clientX - startPointerX;
        const dy = e.clientY - startPointerY;
        if (!active && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        if (!active) {
            active = true;
            panel.classList.add('dragging');
            raisePane(panel);
            lockBodySelection();
        }
        writeOffset(panel, startDragX + dx, startDragY + dy);
    });

    const onUp = (e: PointerEvent): void => {
        if (capturedPointerId !== e.pointerId) return;
        endDrag(true);
    };
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);

    window.addEventListener('blur', () => endDrag(false));
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) endDrag(false);
    });

    return { wasDragging: () => recentlyDragged };
}

/** Non-destructive fit: only shifts the pane if it currently overflows the
 *  viewport. If the pane is larger than the viewport on an axis, pins its
 *  top-left to `VIEWPORT_MARGIN_PX` so the chevron / pane header stays
 *  visible. Returns true if the pane was moved. Preserves `data-edge`
 *  anchors so subsequent collapse/expand grows in the same direction. */
export function clampPaneToViewport(panel: HTMLElement): boolean {
    // Skip hidden panes (`display:none`). Their bounding rect collapses to
    // 0×0 at (0,0), which the clamp logic interprets as "outside the
    // viewport" and pushes to (VIEWPORT_MARGIN_PX, VIEWPORT_MARGIN_PX) —
    // pinning the pane to the top-left corner. A pane that re-shows after
    // being toggled hidden would then re-emerge in the corner instead of
    // wherever it was, or wherever CSS / show-time JS would place it.
    if (getComputedStyle(panel).display === 'none') return false;
    const rect = rectOf(panel);
    const W = window.innerWidth, H = window.innerHeight;
    let nextLeft: number, nextTop: number;

    if (rect.width > W - 2 * VIEWPORT_MARGIN_PX) {
        nextLeft = VIEWPORT_MARGIN_PX;
    } else {
        nextLeft = clamp(rect.left, VIEWPORT_MARGIN_PX, W - rect.width - VIEWPORT_MARGIN_PX);
    }
    if (rect.height > H - 2 * VIEWPORT_MARGIN_PX) {
        nextTop = VIEWPORT_MARGIN_PX;
    } else {
        nextTop = clamp(rect.top, VIEWPORT_MARGIN_PX, H - rect.height - VIEWPORT_MARGIN_PX);
    }

    if (Math.abs(nextLeft - rect.left) < 0.5 && Math.abs(nextTop - rect.top) < 0.5) return false;

    const edge = panel.dataset.edge as PaneEdge | undefined;
    const { h, v } = anchorsForEdge(edge ?? null);
    setInlineAnchors(panel, rect, h, v, nextLeft, nextTop);
    return true;
}

/** Keep a free-floating panel (welcome / help modal) reachable: at least
 *  MIN_HANDLE_VISIBLE_PX of the drag handle stays inside the viewport
 *  horizontally, and the full handle stays inside vertically. Translates
 *  the panel's inline left/top by the minimum amount needed to satisfy
 *  the rule — symmetric on both axes, no edge snap. Returns true if the
 *  panel was moved. */
export function clampFreePanelByHandle(panel: HTMLElement, handle: HTMLElement): boolean {
    const pRect = rectOf(panel);
    const hRect = rectOf(handle);
    const W = window.innerWidth, H = window.innerHeight;
    const M = VIEWPORT_MARGIN_PX;
    // Defensive: can't require more grip area than the handle is wide.
    const G = Math.min(MIN_HANDLE_VISIBLE_PX, hRect.width);

    let dx = 0, dy = 0;
    // Horizontal: ≥ G px of the handle must overlap [M, W − M].
    if      (hRect.right < M + G)     dx = (M + G) - hRect.right;
    else if (hRect.left  > W - M - G) dx = (W - M - G) - hRect.left;
    // Vertical: full handle rect must sit inside [M, H − M]. Header is
    // short (~50px); any vertical clip would lose most of the grip.
    if      (hRect.top    < M)        dy = M - hRect.top;
    else if (hRect.bottom > H - M)    dy = (H - M) - hRect.bottom;

    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return false;
    panel.style.left = `${pRect.left + dx}px`;
    panel.style.top  = `${pRect.top  + dy}px`;
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';
    panel.style.transform = 'none';
    return true;
}

/** Re-snap / re-clamp panes after window resize, preserving edge-anchors.
 *  Docked panes (with `data-edge`) re-snap flush to `EDGE_GAP_PX` from
 *  their edge; all panes are clamped to fit within the viewport. */
export function installResizeClamp(panels: HTMLElement[]): void {
    let scheduled = false;
    window.addEventListener('resize', () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            const W = window.innerWidth, H = window.innerHeight;
            for (const panel of panels) {
                // Only touch panes that have already adopted inline positioning.
                const hasInline = panel.style.left || panel.style.right || panel.style.top || panel.style.bottom;
                if (!hasInline) continue;
                const rect = rectOf(panel);
                const edge = panel.dataset.edge as PaneEdge | undefined;
                const { h, v } = anchorsForEdge(edge ?? null);
                let nextLeft = rect.left, nextTop = rect.top;
                if (edge === 'left')   nextLeft = EDGE_GAP_PX;
                if (edge === 'right')  nextLeft = W - rect.width - EDGE_GAP_PX;
                if (edge === 'top')    nextTop  = EDGE_GAP_PX;
                if (edge === 'bottom') nextTop  = H - rect.height - EDGE_GAP_PX;
                nextLeft = clamp(nextLeft, VIEWPORT_MARGIN_PX, W - rect.width - VIEWPORT_MARGIN_PX);
                nextTop  = clamp(nextTop,  VIEWPORT_MARGIN_PX, H - rect.height - VIEWPORT_MARGIN_PX);
                setInlineAnchors(panel, rect, h, v, nextLeft, nextTop);
            }
        });
    });
}

/** Window-resize counterpart of `clampFreePanelByHandle`. Skipped while
 *  the panel is still using its CSS centering transform (no inline pos
 *  yet), since CSS naturally re-centers it on viewport resize. Once the
 *  user has dragged or resized the panel, this listener keeps it in
 *  reach if the browser is shrunk afterwards. Returns a disposer that
 *  removes the window-level resize listener. */
export function installFreeResizeClamp(panel: HTMLElement, handle: HTMLElement): () => void {
    let scheduled = false;
    const onResize = (): void => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            if (panel.style.left === '' && panel.style.top === '') return;
            clampFreePanelByHandle(panel, handle);
        });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
}

export interface FreelyDraggableOpts {
    /** Called on pointerup after a real drag (not a tap). Receives the post-drag
     *  bounding rect of the panel so callers can persist position. Not called on
     *  pointer cancel / abort. */
    onCommit?: (rect: DOMRect) => void;
    /** Descendant selector that suppresses drag initiation. Defaults to
     *  'button, a, input, textarea, select, [data-no-drag]'. Pass a narrower
     *  set to let some controls (e.g. help tabs, which are <button>s) start a
     *  drag while keeping others (the close button) click-only. A real drag
     *  that starts on such a control has its trailing click suppressed. */
    noDragSelector?: string;
}

const DEFAULT_NO_DRAG = 'button, a, input, textarea, select, [data-no-drag]';

/** Drag a free-floating panel that lives outside the `.pane` system
 *  (e.g. the welcome / help modal). Uses inline `left` / `top` so it
 *  cleanly overrides any centering transform on first drag, then stays
 *  where the user drops it. Viewport-clamped; no edge snap, no chevron,
 *  no `data-edge` chrome. Returns a disposer that unwires every
 *  listener this call attached. */
export function makeFreelyDraggable(panel: HTMLElement, handle: HTMLElement, opts: FreelyDraggableOpts = {}): () => void {
    let pointerX = 0, pointerY = 0;
    let startLeft = 0, startTop = 0;
    let active = false;
    let capturedId: number | null = null;
    let recentlyDragged = false;
    const noDrag = opts.noDragSelector ?? DEFAULT_NO_DRAG;

    // Click-to-front: any pointer interaction with the modal raises it
    // above other floating windows. Capture phase + listening on the panel
    // (not just the handle) so clicks on the header, content, tabs, or
    // resize handle all promote. raisePane respects the CSS baseline, so
    // a modal with `z-index: 1002` stays at 1002 — the counter advance is
    // just bookkeeping for future modals that might share that layer.
    const onPanelPointerDown = (): void => raisePane(panel);
    panel.addEventListener('pointerdown', onPanelPointerDown, { capture: true });

    const onHandlePointerDown = (e: PointerEvent): void => {
        if (!e.isPrimary) return;
        const target = e.target as Element | null;
        if (target?.closest(noDrag)) return;
        const r = rectOf(panel);
        pointerX = e.clientX;
        pointerY = e.clientY;
        startLeft = r.left;
        startTop = r.top;
        capturedId = e.pointerId;
        // Deliberately NOT capturing here: setPointerCapture on pointerdown
        // retargets the trailing `click` to the handle, swallowing clicks on
        // draggable children (help tabs). Capture only once a drag actually
        // starts (in pointermove, below) so a clean tap still clicks through.
    };
    handle.addEventListener('pointerdown', onHandlePointerDown);

    const onHandlePointerMove = (e: PointerEvent): void => {
        if (capturedId !== e.pointerId) return;
        const dx = e.clientX - pointerX;
        const dy = e.clientY - pointerY;
        if (!active && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        if (!active) {
            active = true;
            // Capture now that it's a real drag, so moves keep arriving even if
            // the pointer leaves the handle. Deferred from pointerdown to avoid
            // swallowing the click on a clean tap (see onHandlePointerDown).
            try { handle.setPointerCapture(e.pointerId); } catch { /* stale pointer */ }
            // Lock the visual position via inline anchors and clear any
            // centering transform; from here on, left/top fully control
            // position and transform stays identity.
            panel.style.left = `${startLeft}px`;
            panel.style.top = `${startTop}px`;
            panel.style.right = 'auto';
            panel.style.bottom = 'auto';
            panel.style.transform = 'none';
            panel.classList.add('dragging');
            lockBodySelection();
        }
        // Write the new position, then clamp the handle into view so the
        // panel is always reachable — at least MIN_HANDLE_VISIBLE_PX of
        // the grip strip stays inside the viewport (full handle vertically).
        // The cursor can keep moving past the edge; the panel just stops.
        panel.style.left = `${startLeft + dx}px`;
        panel.style.top  = `${startTop  + dy}px`;
        clampFreePanelByHandle(panel, handle);
    };
    handle.addEventListener('pointermove', onHandlePointerMove);

    const end = (e: PointerEvent, commit: boolean): void => {
        if (capturedId !== e.pointerId) return;
        try { handle.releasePointerCapture(capturedId); } catch { /* already released */ }
        capturedId = null;
        if (active) {
            active = false;
            panel.classList.remove('dragging');
            unlockBodySelection();
            // A drag that began on an otherwise-clickable control (e.g. a help
            // tab) would fire a trailing click on pointerup; swallow it so the
            // drag doesn't also activate the control. Reset next tick — the
            // click dispatches before the macrotask. Pure taps never set this.
            recentlyDragged = true;
            setTimeout(() => { recentlyDragged = false; }, 0);
            if (commit && opts.onCommit) opts.onCommit(rectOf(panel));
        }
    };
    const onHandlePointerUp = (e: PointerEvent): void => end(e, true);
    const onHandlePointerCancel = (e: PointerEvent): void => end(e, false);
    handle.addEventListener('pointerup', onHandlePointerUp);
    handle.addEventListener('pointercancel', onHandlePointerCancel);

    const onClickCapture = (e: MouseEvent): void => {
        if (!recentlyDragged) return;
        e.preventDefault();
        e.stopPropagation();
        recentlyDragged = false;
    };
    panel.addEventListener('click', onClickCapture, { capture: true });

    return () => {
        panel.removeEventListener('click', onClickCapture, { capture: true } as EventListenerOptions);
        // `removeEventListener`'s third arg is typed as `boolean | EventListenerOptions`,
        // but the lib.dom overload picked here narrows to `boolean`; the cast preserves
        // the capture-phase identity needed to match the registration above.
        panel.removeEventListener('pointerdown', onPanelPointerDown, { capture: true } as EventListenerOptions);
        handle.removeEventListener('pointerdown', onHandlePointerDown);
        handle.removeEventListener('pointermove', onHandlePointerMove);
        handle.removeEventListener('pointerup', onHandlePointerUp);
        handle.removeEventListener('pointercancel', onHandlePointerCancel);
    };
}

export interface ResizableOpts {
    /** Lower bound for the panel width, in CSS pixels. */
    minWidth?: number;
    /** Lower bound for the panel height, in CSS pixels. */
    minHeight?: number;
    /** Called on pointerup after a real resize, with the post-resize width/height
     *  in CSS pixels. Not called on pointercancel / blur abort or on dblclick-reset. */
    onCommit?: (size: { width: number; height: number }) => void;
    /** Called on dblclick-reset, after inline width/height have been cleared. */
    onReset?: () => void;
}

/** SE-corner resize for a free-floating panel (currently the help modal).
 *  Mirrors makeFreelyDraggable's pointer-capture skeleton: 4px activation
 *  threshold, pointer capture for stray fingers / off-element drags, abort
 *  on blur / visibility hide. Writes inline width/height in px (clears any
 *  CSS default sizing). Upper bound is the viewport minus VIEWPORT_MARGIN_PX
 *  on each axis, recomputed every move so dragging into the edge stops at
 *  the viewport rather than overflowing. Double-click clears the inline
 *  dims to restore the stylesheet defaults. Returns a disposer that
 *  unwires every listener — including the window/document-level abort
 *  hooks that would otherwise outlive the element. */
export function makeResizableFromCorner(panel: HTMLElement, handle: HTMLElement, opts: ResizableOpts = {}): () => void {
    const minW = opts.minWidth ?? 200;
    const minH = opts.minHeight ?? 160;
    let pointerX = 0, pointerY = 0;
    let startW = 0, startH = 0;
    let active = false;
    let capturedId: number | null = null;

    const endGesture = (commit: boolean): void => {
        if (capturedId !== null) {
            try { handle.releasePointerCapture(capturedId); } catch { /* already released */ }
            capturedId = null;
        }
        if (!active) return;
        active = false;
        panel.classList.remove('resizing');
        unlockBodySelection();
        if (commit && opts.onCommit) opts.onCommit({ width: panel.offsetWidth, height: panel.offsetHeight });
    };

    const onHandlePointerDown = (e: PointerEvent): void => {
        if (!e.isPrimary) return;
        e.stopPropagation();  // don't let the outer pane's drag handler see this
        // First gesture: if the panel is still using a centering transform
        // (e.g. translate(-50%, -50%) on the unmoved help modal), lock it to
        // inline top/left at its current rect and clear the transform — same
        // trick as makeFreelyDraggable. Without this, the centering transform
        // keeps the panel re-centered as it grows, so the SE corner moves at
        // half the pointer's rate instead of tracking 1:1.
        const cs = getComputedStyle(panel);
        if (cs.transform && cs.transform !== 'none' && cs.transform !== 'matrix(1, 0, 0, 1, 0, 0)') {
            const r = rectOf(panel);
            panel.style.left = `${r.left}px`;
            panel.style.top = `${r.top}px`;
            panel.style.right = 'auto';
            panel.style.bottom = 'auto';
            panel.style.transform = 'none';
        }
        pointerX = e.clientX;
        pointerY = e.clientY;
        startW = panel.offsetWidth;
        startH = panel.offsetHeight;
        capturedId = e.pointerId;
        handle.setPointerCapture(e.pointerId);
    };

    const onHandlePointerMove = (e: PointerEvent): void => {
        if (capturedId !== e.pointerId) return;
        const dx = e.clientX - pointerX;
        const dy = e.clientY - pointerY;
        if (!active && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        if (!active) {
            active = true;
            panel.classList.add('resizing');
            raisePane(panel);
            lockBodySelection();
        }
        const W = window.innerWidth, H = window.innerHeight;
        const maxW = W - 2 * VIEWPORT_MARGIN_PX;
        const maxH = H - 2 * VIEWPORT_MARGIN_PX;
        panel.style.width  = `${clamp(startW + dx, minW, maxW)}px`;
        panel.style.height = `${clamp(startH + dy, minH, maxH)}px`;
    };

    const onHandlePointerUp = (): void => endGesture(true);
    const onHandlePointerCancel = (): void => endGesture(false);
    const onWindowBlur = (): void => endGesture(false);
    const onVisibilityChange = (): void => { if (document.hidden) endGesture(false); };

    // Double-click / double-tap → reset width/height to stylesheet defaults.
    // Note: leaves inline top/left/transform alone, since the user may have
    // intentionally dragged the panel before resizing. To also recenter, the
    // user should close & reopen the modal — or call /reset gesture later.
    const onHandleDblClick = (e: MouseEvent): void => {
        e.stopPropagation();
        panel.style.width = '';
        panel.style.height = '';
        if (opts.onReset) opts.onReset();
    };

    handle.addEventListener('pointerdown', onHandlePointerDown);
    handle.addEventListener('pointermove', onHandlePointerMove);
    handle.addEventListener('pointerup', onHandlePointerUp);
    handle.addEventListener('pointercancel', onHandlePointerCancel);
    window.addEventListener('blur', onWindowBlur);
    document.addEventListener('visibilitychange', onVisibilityChange);
    handle.addEventListener('dblclick', onHandleDblClick);

    return () => {
        handle.removeEventListener('pointerdown', onHandlePointerDown);
        handle.removeEventListener('pointermove', onHandlePointerMove);
        handle.removeEventListener('pointerup', onHandlePointerUp);
        handle.removeEventListener('pointercancel', onHandlePointerCancel);
        window.removeEventListener('blur', onWindowBlur);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        handle.removeEventListener('dblclick', onHandleDblClick);
    };
}

/** Auto-fit a pane to the viewport whenever its size changes (collapse,
 *  expand, content reflow). Uses a ResizeObserver with a trailing debounce
 *  so the clamp runs once after CSS transitions settle, not every frame. */
export function installSizeClamp(panel: HTMLElement, debounceMs = 100): () => void {
    let pending: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver(() => {
        if (pending) clearTimeout(pending);
        pending = setTimeout(() => { pending = null; clampPaneToViewport(panel); }, debounceMs);
    });
    ro.observe(panel);
    return () => {
        if (pending) clearTimeout(pending);
        ro.disconnect();
    };
}
